import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CacheService } from '../common/services/cache.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CACHE_KEYS } from '../common/constants/cache-keys';
import { LPMI_ROUTES } from '../common/constants/routes';
import { LoginService } from '../login/login.service';
import { AuthResponse, GetAllRolesResponse, LanguageResponse, Languages, LoginRequest, LoginResults, LoginSelections, UserLookupResults } from '../login/login.viewmodel';
import { AuthService } from '../common/services/auth/auth.service';
import { FormGroup, FormControl, Validators, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LoginLinks, PageJsonResults } from '../common/services/page/page-data.viewmodel';
import { addFragmentAndReload, getLanguageFromLanguageCode, getUrlByCategory, isLPMIHost } from '../common/util/utils';
import { InputComponent } from '../common/components/general/input/input.component';
import { LinkButtonComponent } from '../common/components/general/link-button/link-button.component';
import { ButtonComponent } from '../common/components/general/button/button.component';
import { LanguageSwitcherComponent } from '../common/components/language-switcher/language-switcher.component';
import { IconComponent } from '../common/components/general/icon/icon.component';
import { CommonModule, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { COMPONENT_META_DATA } from '../common/constants/component-meta-data';
import { ContentModel } from '../common/models/content.model';
import { ContentFilterPipe } from '../common/pipes/content-filter.pipe';
import { ErrorData } from '../common/models/error.model';
import { Language } from '../common/components/language-switcher/language-switcher.model';
import { PageService } from '../common/services/page/page.service';
import { SelectOption } from '../common/components/general/select/select.model';
import { SettingsService } from '../common/services/settings.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'nuc-login-redirect',
  imports: [ ReactiveFormsModule, CommonModule, ContentFilterPipe],
  templateUrl: './login-redirect.component.html',
  styleUrl: './login-redirect.component.scss',
})
export class LoginRedirectComponent implements OnInit {
  errorMessage: string;
  userDetailsResponse: LoginResults;
  userName: string;
  userFirstName: string;
  userEmail: string;
  redirectUrl: string;
  tncLink: LoginLinks;
  loginResponse: LoginResults = {};
  withoutTNC: LoginLinks[] = [];
  loginLinks: LoginLinks[] = [];
  authenticationForm: FormGroup = new FormGroup({});
  enableAuth: boolean = false;
  timerDisplay: string = '01:00';
  private timerSubscription: Subscription = new Subscription();
  authResponse: AuthResponse = {};
  enableLogin: boolean = false;
  LABELS = COMPONENT_META_DATA.AUTH.LABELS;
  componentType: string = COMPONENT_META_DATA.AUTH.VALUE;
  pageContent: ContentModel[];
  userRolesTriggered: boolean = false;
  disableResend: boolean = false;
  isPasswordField: boolean = false;
  displayWaitMessage: boolean = false;
  loading: boolean = true;
  bgImageUrl: string;
  stellLogo: string;
  countryCode: string = '';
  otpExpiredMessage: string;
  otpSuccess: boolean = true;
  articleRedirectUrl : string;
  helpAreaRedirectUrl: string;
  constructor(private router: Router, private route: ActivatedRoute, private loginService: LoginService, private authService: AuthService, private cacheService: CacheService, private formBuilder: FormBuilder, private cdRef: ChangeDetectorRef,
    private settingsService: SettingsService) {
    this.articleRedirectUrl = this.cacheService.getFromCache(CACHE_KEYS.ARTICLE.REDIRECT_URL);
    this.helpAreaRedirectUrl = this.cacheService.getFromCache(CACHE_KEYS.HELP_AREA_INFO.REDIRECT_URL);
  }
  ngOnInit(): void {
    if (this.cacheService.getFromCache(CACHE_KEYS.LOGIN.ACCESS_TOKEN)) {
      this.userRolesTriggered = true;
      this.getUserInformationAndRedirect();
    }
    this.authService.getAccessToken$().subscribe({
      next: (accessToken) => {
        if (accessToken && !this.userRolesTriggered) {
          this.userRolesTriggered = true;
          this.getUserInformationAndRedirect();
        } else {
          // Handle case when accessToken is still not available (e.g., show a loading indicator)
          console.log('Access Token is not yet available.');
        }
      },
      error: (err) => {
        // Handle errors, such as token retrieval failure
        console.error('Error retrieving access token:', err);
      },
    });
  }

  private init() {
    let routerPageData: PageJsonResults = this.route.snapshot.data['pageData'];
    this.pageContent = this.loginService.getContent(this.componentType, routerPageData);
  }

  generateRandomInSameRange(domain: number): number {
    if (domain >= 100 && domain <= 199) {
      return this.getRandomInRange(100, 199);
    } else if (domain >= 200 && domain <= 299) {
      return this.getRandomInRange(200, 299);
    } else if (domain >= 300 && domain <= 399) {
      return this.getRandomInRange(300, 399);
    } else if (domain >= 400 && domain <= 499) {
      return this.getRandomInRange(400, 499);
    } else {
      console.error('Domain is out of expected range:', domain);
      return -1; // Handle unexpected cases
    }
  }

  getRandomInRange(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private getUserInformationAndRedirect() {
    let results: UserLookupResults = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.USER_LOOKUP_RES) as UserLookupResults;
    this.errorMessage = '';

    let loginRequest = new LoginRequest();
    loginRequest.userName = this.userName || results?.username;
    loginRequest.userType = results?.userType;
    loginRequest.domain = this.generateRandomInSameRange(results?.domain);

    const cachedLoginData: LoginResults = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA) as LoginResults;
    if (cachedLoginData?.dealerLists?.length) {
      this.processLoginResponse(cachedLoginData);
      return;
    }

    const rolesObservable = this.loginService.getLpmiUserRoles();

    rolesObservable.subscribe({
      next: (response) => {
        this.processLoginResponse(response?.results);
      },
      error: (err: ErrorData) => {
        if (err?.errorList && err?.httpStatus == '412') {
          const error = err.errorList.find((e) => e.errorCode === '412');
          this.errorMessage = error ? error.errorMsg : 'An unknown error occurred';
          this.cacheService.clearCache();
        } else {
          this.errorMessage = 'An unknown error occurred';
          this.cacheService.clearCache();
        }
      },
    });
  }

  private processLoginResponse(loginData: LoginResults) {
    let loginSelections: LoginSelections = this.getLoginSelectionsFromLoginPreference(loginData);
    if (loginSelections) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS, loginSelections);
      this.getLanguagesByMarketAndProcess(loginData, loginSelections);
    } else {
      if (loginData?.isAdmin && loginData?.userType === 'LeapMotor') {
        if (loginData?.dealerLists?.length) {
          let primaryOrFirstDealer = loginData?.dealerLists?.find((dlr) => dlr?.primaryDealer) || loginData?.dealerLists[0];
          if (primaryOrFirstDealer) {
            let role = primaryOrFirstDealer?.roleList[0];
            if (role) {
              const loginSelections = LoginSelections.create(role.businessOp, role.businessOpLabel, role.roleName, role.roleLabel, primaryOrFirstDealer.dealerCode, primaryOrFirstDealer.dealerShipName);
              loginSelections.isIR = loginData?.isIndependentRepairer;
              loginSelections.isAdmin = loginData?.isAdmin;
              loginSelections.market = loginData?.market?.toLowerCase();
              loginSelections.region = loginData?.region?.toLowerCase();
              loginSelections.userId = loginData?.userPreferences?.userId;
              this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS, loginSelections);
              this.getLanguagesByMarketAndProcess(loginData, loginSelections);
            }
          }
        }
      }
     else {
        loginSelections = this.getLoginSelectionsForNonAdmin(loginData);
        if (!loginSelections) {
          this.errorMessage = 'No Associated Dealers Found for Non-Admin User';
          return;
        }
        this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS, loginSelections);
        this.getLanguagesByMarketAndProcess(loginData, loginSelections);
      }
    }
  }

  private getLoginSelectionsForNonAdmin(loginData: LoginResults): LoginSelections {
    if (loginData?.dealerLists?.length) {
      let primaryOrFirstDealer = loginData?.dealerLists?.find((dlr) => dlr?.primaryDealer) || loginData?.dealerLists[0];
      if (primaryOrFirstDealer?.dealerCode) {
        let role = primaryOrFirstDealer?.roleList[0];
        if (role) {
          const loginSelections = LoginSelections.create(role.businessOp, role.businessOpLabel, role.roleName, role.roleLabel, primaryOrFirstDealer.dealerCode, primaryOrFirstDealer.dealerShipName);
          loginSelections.isIR = loginData?.isIndependentRepairer;
          loginSelections.isAdmin = false;
          loginSelections.market = loginData?.market?.toLowerCase();
          loginSelections.region = loginData?.region?.toLowerCase();
          loginSelections.userId = loginData?.userPreferences?.userId;
          return loginSelections;
        }
      }
    }
    return null;
  }

  private getLoginSelectionsFromLoginPreference(loginData: LoginResults): LoginSelections {
    const loginPreference = loginData?.userPreferences?.preferences?.loginPreference;
    if (loginPreference?.businessOp && (loginPreference?.roleName || loginPreference?.roleName == '')) {
      if (loginData.isAdmin) {
        const loginSelections: LoginSelections = new LoginSelections();
        loginSelections.market = loginPreference?.market?.toLowerCase();
        loginSelections.region = loginPreference?.region?.toLowerCase();
        loginSelections.isAdmin = loginPreference?.isAdmin;
        loginSelections.isIR = loginPreference?.isIR;
        loginSelections.userId = loginData?.userPreferences?.userId;
        loginSelections.selectedBusinessLabel = new SelectOption(loginPreference.businessOp, loginPreference.businessOpLabel);
        //TODO: CHECK LOGIC FOR DEALER
        loginSelections.selectedDealer = new SelectOption(`${loginPreference.dealerCode} - ${loginPreference.dealerShipName}`, `${loginPreference.dealerCode} - ${loginPreference.dealerShipName}`);
        loginSelections.selectedRole = new SelectOption(loginPreference.roleName, loginPreference.roleLabel);
        return loginSelections;
      } else {
        const dealer = loginData?.dealerLists?.find((dlr) => `${dlr.dealerCode} - ${dlr.dealerShipName}` == loginPreference.dealerCode);
        if (dealer) {
          const role = dealer?.roleList?.find((rl) => rl.businessOp == loginPreference.businessOp && rl.roleName == loginPreference.roleName);
          if (role) {
            const loginSelections = LoginSelections?.create(role.businessOp, role.businessOpLabel, role.roleName, role.roleLabel, dealer.dealerCode, dealer.dealerShipName);
            loginSelections.market = loginPreference?.market?.toLowerCase();
            loginSelections.region = loginPreference?.region?.toLowerCase();
            loginSelections.isAdmin = loginPreference?.isAdmin;
            loginSelections.isIR = loginPreference?.isIR;
            loginSelections.userId = loginData?.userPreferences?.userId;
            return loginSelections;
          }
        }
      }
    }
    return null;
  }

  getLanguagesByMarketAndProcess(loginData: LoginResults, loginSelections: LoginSelections) {
    //checking if cache_language is present in getLanguagesByMarketCode
    const market = loginSelections?.market;
    const region = loginSelections?.region;

    const prefLanguage: Language = getLanguageFromLanguageCode(loginData.userPreferences?.preferences?.loginPreference?.languageCode);
    const cachedLanguage: Language = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);

    this.loginService.getLanguagesByMarketCode(market, region).subscribe((res: LanguageResponse) => {
      if (res?.results?.languages?.length === 0) {
        let finalLanguage: Language =  { countryCode: 'US', languageCode: 'en' };
        if (this.checkIfReloadRequiredAndReload(finalLanguage)) {
          this.init();
          this.enableAuth = this.settingsService.getMfaflag();
          if (!this.enableAuth) {
            this.displayWaitMessage = true;
            if(this.articleRedirectUrl){
              this.router.navigateByUrl(this.cacheService.getFromCache(CACHE_KEYS.ARTICLE.REDIRECT_URL));
            }
            else if(this.helpAreaRedirectUrl){
              this.router.navigateByUrl(this.cacheService.getFromCache(CACHE_KEYS.HELP_AREA_INFO.REDIRECT_URL));
            }
            else{
              this.router.navigate(['/' + LPMI_ROUTES.LANDING.PATH]);
            }
          } else {
            this.userDetailsResponse = loginData;
            if (this.authResponse.success) {
              this.enableLogin = true;
            }
            this.loading = false;
          }
        }
      }
      else {
        //const languages: Languages[] = res.results.languages;
        const marketLanguageCodes: Language[] = res.results?.languages?.map((lng) => getLanguageFromLanguageCode(lng.isoCode));
        let finalLanguage: Language = marketLanguageCodes.find((mLang) => mLang.languageCode == prefLanguage?.languageCode) || marketLanguageCodes.find((mLang) => mLang.languageCode == cachedLanguage?.languageCode);
        if (!finalLanguage) {
          finalLanguage = getLanguageFromLanguageCode(res.results.languages.find((lng) => lng.isDefault)?.isoCode || res.results.languages.find((lng) => lng.languageCode == 'en')?.isoCode);
        }
        if (this.checkIfReloadRequiredAndReload(finalLanguage)) {
          this.init();
          this.enableAuth = this.settingsService.getMfaflag();
          if (!this.enableAuth) {
            this.displayWaitMessage = true;
           if(this.articleRedirectUrl){
              this.router.navigateByUrl(this.cacheService.getFromCache(CACHE_KEYS.ARTICLE.REDIRECT_URL));
            }
            else if(this.helpAreaRedirectUrl){
              this.router.navigateByUrl(this.cacheService.getFromCache(CACHE_KEYS.HELP_AREA_INFO.REDIRECT_URL));
            }
            else{
              this.router.navigate(['/' + LPMI_ROUTES.LANDING.PATH]);
            }
          } else {
            this.userDetailsResponse = loginData;
            if (this.authResponse.success) {
              this.enableLogin = true;
            }
            this.loading = false;
          }
        }
      }
      if (res?.errorData?.httpStatus) {
        this.errorMessage = `Access Denied. '${res?.results?.market.toUpperCase()}' Market configuration is missing`;
        this.cacheService.clearCache();
      }
    });
  }

  // returns true when reload is not required
  private checkIfReloadRequiredAndReload(selectedLanguage: Language): boolean {
    const cache_language: Language = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
    if (selectedLanguage && cache_language && selectedLanguage?.languageCode?.toLowerCase() != cache_language?.languageCode) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LANGUAGE, selectedLanguage);
      //reload
      addFragmentAndReload();
      return false;
    }
    return true;
  }
}
